import React, {
  Suspense, lazy, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PublicIcon from '@mui/icons-material/Public';
import {
  deleteV2App, getV2App, listV2Flows, patchV2AppMeta, updateV2App,
} from '../../api/studioV2.api';
import StudioPageShell from '../../components/studio/StudioPageShell';
import { STUDIO_V2_APP_TEMPLATES, mergeAppTemplateIntoCurrent } from '../../components/studio/studioV2AppTemplates.js';
import { touchStudioRecent } from '../../utils/studioRecents';
import {
  friendlyReadiness, markReviewed, parseAppText, serializeApp,
} from '../../components/studio/appBuilder/appBuilderModel';
import AppDesignPanel from '../../components/studio/appBuilder/AppDesignPanel';
import AppLookPanel from '../../components/studio/appBuilder/AppLookPanel';
import AppLivePreview from '../../components/studio/appBuilder/AppLivePreview';
import AppReadinessButton from '../../components/studio/appBuilder/AppReadinessButton';

const StudioV2AppLayoutCanvas = lazy(() => import('../../components/studio/StudioV2AppLayoutCanvas'));
const StudioV2AppRuntimePreview = lazy(() => import('../../components/studio/StudioV2AppRuntimePreview'));
const StudioV2AppAiChatPanel = lazy(() => import('../../components/studio/StudioV2AppAiChatPanel'));
const StudioV2AppFlowWorkspacePanel = lazy(() => import('../../components/studio/StudioV2AppFlowWorkspacePanel'));
const StudioV2AppRuntimeLedgerPanel = lazy(() => import('../../components/studio/StudioV2AppRuntimeLedgerPanel'));
const StudioV2JsonMonacoEditor = lazy(() => import('../../components/studio/StudioV2JsonMonacoEditor'));
const AppPublishPanel = lazy(() => import('../../components/studio/appBuilder/AppPublishPanel'));

const ADVANCED_STORAGE = 'studio_app_builder_advanced_v1';
const SIMPLE_TABS = ['design', 'look', 'ai', 'publish'];
const ADVANCED_TABS = ['layout', 'workspace', 'preview', 'ledger', 'json'];

const STATUS_CHIP = {
  published: { label: 'Live', color: 'success' },
  draft: { label: 'Draft', color: 'warning' },
  archived: { label: 'Archived', color: 'default' },
};

function Loading() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
      <CircularProgress size={36} />
    </Box>
  );
}

function usePreviewHeight() {
  const calc = () => Math.max(460, Math.min(900, (typeof window !== 'undefined' ? window.innerHeight : 900) - 230));
  const [h, setH] = useState(calc);
  useEffect(() => {
    const onResize = () => setH(calc());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return h;
}

function StudioAppEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [lastSavedText, setLastSavedText] = useState('');
  const [lifecycleStatus, setLifecycleStatus] = useState('draft');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [metaBusy, setMetaBusy] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [accessRole, setAccessRole] = useState('owner');
  const [tab, setTab] = useState('design');
  const [advanced, setAdvanced] = useState(() => {
    try {
      return window.localStorage.getItem(ADVANCED_STORAGE) === '1';
    } catch {
      return false;
    }
  });
  const [layoutEpoch, setLayoutEpoch] = useState(0);
  const [workspaceCanvasKey, setWorkspaceCanvasKey] = useState(0);
  const [previewLive, setPreviewLive] = useState(false);
  const [flows, setFlows] = useState([]);
  const [tplMenuAnchor, setTplMenuAnchor] = useState(null);
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState(null);
  const [readinessSignal, setReadinessSignal] = useState(0);
  const previewHeight = usePreviewHeight();

  const canEdit = accessRole === 'owner' || accessRole === 'editor';
  const canManage = accessRole === 'owner';
  const publishPath = `/v2/p/${id}`;
  const dirty = text !== lastSavedText;

  const def = useMemo(() => parseAppText(text), [text]);
  const deferredText = useDeferredValue(text);
  const previewDef = useMemo(() => parseAppText(deferredText), [deferredText]);
  const readiness = useMemo(() => friendlyReadiness(def), [def]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const { app, error: err } = await getV2App(id);
      if (cancelled) return;
      if (err) {
        setError(err);
        setLoading(false);
        return;
      }
      const loaded = app?.definition || {};
      const serialized = serializeApp(loaded);
      setText(serialized);
      setLastSavedText(serialized);
      setLifecycleStatus(app?.status || 'draft');
      setAccessRole(app?.accessRole || 'owner');
      touchStudioRecent({
        kind: 'app',
        id: String(id),
        name: loaded?.name || app?.name || id,
        href: `/studio/apps/${id}`,
      });
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    listV2Flows().then(({ flows: rows }) => setFlows(rows || []));
  }, []);

  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const onSave = useCallback(async () => {
    if (!canEdit || saving) return;
    setError(null);
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch (e) {
      setError(`The app settings have a formatting problem, so they were not saved (${e.message}).`);
      return;
    }
    setSaving(true);
    const { error: err } = await updateV2App(id, parsed);
    setSaving(false);
    if (err) {
      setError(`Not saved: ${err}`);
      return;
    }
    const serialized = serializeApp(parsed);
    setText(serialized);
    setLastSavedText(serialized);
    setToast(lifecycleStatus === 'published' ? 'Saved. The live app is updated.' : 'Saved');
  }, [canEdit, saving, text, id, lifecycleStatus]);

  const saveRef = useRef(onSave);
  saveRef.current = onSave;
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        saveRef.current();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const onDefChange = (next) => setText(serializeApp(next));

  const openTab = (value) => {
    if (value === 'layout') setLayoutEpoch((n) => n + 1);
    if (value === 'workspace') setWorkspaceCanvasKey((n) => n + 1);
    if (ADVANCED_TABS.includes(value) && !advanced) {
      setAdvanced(true);
      try { window.localStorage.setItem(ADVANCED_STORAGE, '1'); } catch { /* ignore */ }
    }
    setTab(value);
  };

  const toggleAdvanced = (on) => {
    setAdvanced(on);
    try { window.localStorage.setItem(ADVANCED_STORAGE, on ? '1' : '0'); } catch { /* ignore */ }
    if (!on && ADVANCED_TABS.includes(tab)) setTab('design');
  };

  const onFixReadiness = (item) => {
    if (item.fix === 'design') openTab('design');
    else if (item.fix === 'advanced-layout') openTab('layout');
    else if (item.fix === 'advanced-json') openTab('json');
    else if (item.fix === 'review' && def) onDefChange(markReviewed(def, item.id));
  };

  const onDelete = async () => {
    if (!window.confirm('Delete this app? This cannot be undone.')) return;
    const { error: err } = await deleteV2App(id);
    if (err) {
      setError(err);
      return;
    }
    navigate('/studio/apps');
  };

  const onSetStatus = async (next) => {
    if (dirty && canEdit && next !== 'draft') {
      setError('Save your changes first.');
      return;
    }
    setMetaBusy(true);
    setError(null);
    const { app: updated, error: err } = await patchV2AppMeta(id, { status: next });
    setMetaBusy(false);
    if (err) {
      setError(err);
      return;
    }
    setLifecycleStatus(updated?.status || next);
    setToast({
      published: 'Published. Your link is live.',
      archived: 'Archived',
      draft: lifecycleStatus === 'published' ? 'Taken offline. The app is a draft again.' : 'Restored to draft',
    }[next]);
  };

  const onExportAppJson = () => {
    if (!def) {
      setError('The app settings have a formatting problem, so they cannot be exported yet.');
      return;
    }
    const rawName = (def.name && String(def.name).trim()) || def.id || id || 'app';
    const safe = String(rawName).replace(/[^\w-]+/g, '_').slice(0, 80) || 'app';
    const blob = new Blob([serializeApp(def)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${safe}.app.v2.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const onApplyDefinitionFromAi = (nextText) => {
    setText(nextText);
    setLayoutEpoch((n) => n + 1);
    setToast('Applied the AI suggestion. Check the preview, then save.');
  };

  const onPickTemplate = (tpl) => {
    setTplMenuAnchor(null);
    setError(null);
    const { text: merged, error: mergeErr } = mergeAppTemplateIntoCurrent(text, tpl.definition, tpl.id);
    if (mergeErr) {
      setError(mergeErr);
      return;
    }
    setText(merged);
    setLayoutEpoch((n) => n + 1);
    setToast(`Applied the ${tpl.label} template. Save to keep it.`);
  };

  const appName = (def?.name && String(def.name).trim()) || 'Untitled app';
  const statusChip = STATUS_CHIP[lifecycleStatus] || STATUS_CHIP.draft;
  const isSimpleTab = SIMPLE_TABS.includes(tab);
  const badJson = !def && !loading;

  const header = (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
      {!loading && (
        <AppReadinessButton readiness={readiness} onFix={onFixReadiness} canEdit={canEdit} openSignal={readinessSignal} />
      )}
      <Button
        variant={dirty ? 'contained' : 'outlined'}
        onClick={onSave}
        disabled={loading || saving || !canEdit || !dirty}
        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : (!dirty && <CheckIcon />)}
        sx={{ textTransform: 'none', fontWeight: 600, minWidth: 150 }}
        data-testid="app-save"
      >
        {saving ? 'Saving…' : dirty ? 'Save changes' : 'All changes saved'}
      </Button>
      {lifecycleStatus !== 'published' && (
        <Button
          variant="contained"
          color="secondary"
          startIcon={<PublicIcon />}
          disabled={loading}
          onClick={() => openTab('publish')}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Publish
        </Button>
      )}
      <IconButton onClick={(e) => setActionsMenuAnchor(e.currentTarget)} disabled={loading} aria-label="More app actions">
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={actionsMenuAnchor} open={Boolean(actionsMenuAnchor)} onClose={() => setActionsMenuAnchor(null)}>
        <MenuItem
          disabled={!canEdit}
          onClick={(e) => {
            setActionsMenuAnchor(null);
            setTplMenuAnchor(e.currentTarget);
          }}
        >
          <ListItemIcon><ViewQuiltIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Start from a template…</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setActionsMenuAnchor(null);
            onExportAppJson();
          }}
        >
          <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Download a copy</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          disabled={!canManage}
          onClick={() => {
            setActionsMenuAnchor(null);
            onDelete();
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon><DeleteOutlineIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Delete app</ListItemText>
        </MenuItem>
      </Menu>
      <Menu anchorEl={tplMenuAnchor} open={Boolean(tplMenuAnchor)} onClose={() => setTplMenuAnchor(null)}>
        {STUDIO_V2_APP_TEMPLATES.map((tpl) => (
          <MenuItem key={tpl.id} onClick={() => onPickTemplate(tpl)} sx={{ maxWidth: 380, whiteSpace: 'normal' }}>
            <ListItemText primary={tpl.label} secondary={tpl.description} />
          </MenuItem>
        ))}
      </Menu>
    </Stack>
  );

  const simpleContent = () => {
    if (badJson && tab !== 'publish' && tab !== 'ai') {
      return (
        <Alert
          severity="warning"
          action={<Button color="inherit" size="small" onClick={() => openTab('json')}>Open JSON</Button>}
        >
          The app settings have a formatting problem, so the simple editor cannot show them. Fix them in the JSON view.
        </Alert>
      );
    }
    if (tab === 'design') {
      return <AppDesignPanel def={def} flows={flows} canEdit={canEdit} onChange={onDefChange} onOpenAdvanced={openTab} />;
    }
    if (tab === 'look') return <AppLookPanel def={def} canEdit={canEdit} onChange={onDefChange} />;
    if (tab === 'ai') {
      return canEdit ? (
        <Suspense fallback={<Loading />}>
          <StudioV2AppAiChatPanel
            appMongoId={id}
            definitionText={text}
            hasUnsavedChanges={dirty}
            onApplyDefinition={onApplyDefinitionFromAi}
          />
        </Suspense>
      ) : <Alert severity="info">The AI assistant is available to owners and editors.</Alert>;
    }
    return (
      <Suspense fallback={<Loading />}>
        <AppPublishPanel
          appMongoId={id}
          status={lifecycleStatus}
          busy={metaBusy}
          dirty={dirty}
          canEdit={canEdit}
          readiness={readiness}
          publishPath={publishPath}
          onSetStatus={onSetStatus}
          onOpenReadiness={() => setReadinessSignal((n) => n + 1)}
          onNotify={setToast}
          text={text}
          setText={setText}
          onDefinitionRestored={() => setLayoutEpoch((n) => n + 1)}
          accessRole={accessRole}
        />
      </Suspense>
    );
  };

  const advancedContent = () => (
    <Suspense fallback={<Loading />}>
      {tab === 'layout' && <StudioV2AppLayoutCanvas key={layoutEpoch} initialText={text} onTextChange={setText} />}
      {tab === 'workspace' && (
        <StudioV2AppFlowWorkspacePanel appText={text} setAppText={setText} appCanvasKey={workspaceCanvasKey} />
      )}
      {tab === 'preview' && <StudioV2AppRuntimePreview definitionText={text} />}
      {tab === 'ledger' && <StudioV2AppRuntimeLedgerPanel appMongoId={id} />}
      {tab === 'json' && <StudioV2JsonMonacoEditor value={text} onChange={setText} schemaKind="app" />}
    </Suspense>
  );

  return (
    <StudioPageShell
      title={(
        <Stack direction="row" spacing={1.25} alignItems="center" component="span">
          <span data-testid="app-title">{loading ? 'Loading…' : appName}</span>
          {!loading && (
            <Chip
              size="small"
              label={statusChip.label}
              color={statusChip.color}
              variant={lifecycleStatus === 'published' ? 'filled' : 'outlined'}
              sx={{ fontWeight: 600 }}
              data-testid="app-status"
            />
          )}
        </Stack>
      )}
      backTo="/studio/apps"
      backLabel="All apps"
      maxWidth={1600}
      actions={header}
    >
      <Stack spacing={2}>
        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
        {!loading && !canEdit && (
          <Alert severity="info">
            You can look around and try the app, but only its owner and editors can change it.
          </Alert>
        )}
        {loading ? <Loading /> : (
          <>
            <Stack direction="row" alignItems="center" sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={tab}
                onChange={(_e, v) => openTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ flex: 1, minWidth: 0, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 48 } }}
              >
                <Tab value="design" label="Design" />
                <Tab value="look" label="Look & feel" />
                <Tab value="ai" label="AI assistant" />
                <Tab value="publish" label="Publish" />
                {advanced && <Tab value="layout" label="Layout details" sx={{ color: 'text.secondary' }} />}
                {advanced && <Tab value="workspace" label="Flows" sx={{ color: 'text.secondary' }} />}
                {advanced && <Tab value="preview" label="Test runs" sx={{ color: 'text.secondary' }} />}
                {advanced && <Tab value="ledger" label="Activity" sx={{ color: 'text.secondary' }} />}
                {advanced && <Tab value="json" label="JSON" sx={{ color: 'text.secondary' }} />}
              </Tabs>
              <Tooltip title="Show technical tools: detailed field mapping, flow canvas, run history and raw JSON">
                <FormControlLabel
                  sx={{ ml: 1, mr: 0, flex: '0 0 auto' }}
                  control={<Switch size="small" checked={advanced} onChange={(e) => toggleAdvanced(e.target.checked)} />}
                  label={<Typography variant="body2" color="text.secondary">Advanced</Typography>}
                />
              </Tooltip>
            </Stack>

            {isSimpleTab ? (
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', lg: 'minmax(400px, 5fr) 7fr' },
                gap: 3,
                alignItems: 'start',
              }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, sm: 3 }, borderRadius: 3, border: 1, borderColor: 'divider', minWidth: 0,
                  }}
                >
                  {simpleContent()}
                </Paper>
                <Box sx={{ position: { lg: 'sticky' }, top: { lg: 16 }, minWidth: 0 }}>
                  <AppLivePreview
                    definition={previewDef}
                    mongoId={id}
                    height={previewHeight}
                    live={previewLive}
                    onLiveChange={setPreviewLive}
                  />
                </Box>
              </Box>
            ) : (
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.5, sm: 2.5 }, borderRadius: 3, border: 1, borderColor: 'divider',
                }}
              >
                {advancedContent()}
              </Paper>
            )}
          </>
        )}
      </Stack>
      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3500}
        onClose={() => setToast(null)}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </StudioPageShell>
  );
}

export default StudioAppEditPage;
